/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useEffect, useState } from 'react';
import { Box, Checkbox, Stack, TableBody, TableRow } from '@mui/material';
import CustomTableCell from 'components/cmn/CustomTableCell';
import { ICmnTableBodyProps } from 'interface/cmn/commonInfo';
import { AccountsContent, IProjectAdmin } from 'interface/project/projectInfo';
import { getComparatorAny, stableSort } from 'libs/mindUtils';
import { useProject } from 'hooks/useProject';
import { changeStepper1Value } from 'store/project/projectSlice';
import { useAppDispatch } from 'store';
import ProjectRoleBox from './ProjectRoleBox';

function CompAccountsTableBody({ loading, rows }: ICmnTableBodyProps<AccountsContent>): JSX.Element {
  const dispatch = useAppDispatch();
  const { tmpStepper1 } = useProject();
  const { projectAdmins } = tmpStepper1;
  const [selected, setSelected] = useState<IProjectAdmin[]>([]);

  useEffect(() => {
    setSelected(projectAdmins);
    dispatch(changeStepper1Value({ tmpProjectAdmins: projectAdmins }));
  }, [dispatch, projectAdmins]);

  const isSelected = (admin: IProjectAdmin): boolean => selected.some(item => item.id === admin.id);
  const handleConvertAdmin = (account: AccountsContent): IProjectAdmin => {
    return {
      id: account.id,
      email: account.email,
      name: account.name,
      mobile: account.mobile,
      compName: account.compName,
      compBusinessNumber: account.compBizNum,
    };
  };

  const handleChange = (_row: AccountsContent) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const admin = handleConvertAdmin(_row);

    if (event.target.checked) {
      setSelected(prevSelected => {
        const newSelected = [...prevSelected, admin];
        updateCompAdminList(newSelected);
        return newSelected;
      });
    } else {
      setSelected(prevSelected => {
        const newSelected = prevSelected.filter(item => item.id !== admin.id);
        updateCompAdminList(newSelected);
        return newSelected;
      });
    }
  };
  const updateCompAdminList = (newSelected: IProjectAdmin[]) => {
    dispatch(
      changeStepper1Value({
        tmpProjectAdmins: newSelected,
      }),
    );
  };
  return (
    <>
      {!loading && rows.length === 0 && (
        <TableBody>
          <TableRow>
            <CustomTableCell align="center" colSpan={5} sx={{ border: 'none' }}>
              조회된 데이터가 없습니다.
            </CustomTableCell>
          </TableRow>
        </TableBody>
      )}
      {!loading && rows.length !== 0 && (
        <TableBody>
          {stableSort(rows, getComparatorAny('desc', 'id')).map((row, index) => {
            const isItemSelected = isSelected(handleConvertAdmin(row));
            const labelId = `table-checkbox-${index}`;

            return (
              <TableRow
                hover
                role="checkbox"
                aria-checked={isItemSelected}
                tabIndex={-1}
                key={row.id}
                selected={isItemSelected}
                id={labelId}
              >
                <CustomTableCell align="center">
                  <Stack direction="column" alignItems="flex-start" sx={{ display: 'flex' }} spacing={1}>
                    <Box sx={{ typography: 's14w600' }}>{row.name}</Box>
                    <Box sx={{ typography: 's12w400', color: 'mind.custom.description' }}>{row.email}</Box>
                  </Stack>
                </CustomTableCell>
                <CustomTableCell align="center">
                  <ProjectRoleBox
                    roleCode={row.roles?.[0]?.roleCode || '-'}
                    roleName={row.roles?.[0]?.roleName || '-'}
                  />
                </CustomTableCell>
                <CustomTableCell align="center">
                  <Box sx={{ typography: 's14w400' }}>{row.compName || '-'}</Box>
                </CustomTableCell>
                <CustomTableCell align="center">{row.compRole || '-'}</CustomTableCell>
                <CustomTableCell align="center">
                  <Checkbox checked={isItemSelected} onChange={handleChange(row)} />
                </CustomTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      )}
    </>
  );
}

export default memo(CompAccountsTableBody);
